import { Divider, Drawer, Flex } from 'antd';
import { useState, useCallback, useMemo } from 'react';
import styles from './business-hours-permanent.module.scss';
import { BusinessHoursDay, BusinessHoursDayData } from '../business-hours-day/business-hours-day';
import { Button } from '../button/button';
import { BusinessHourItem } from '@/services/business-hours/business-hours.types';

const WEEKDAYS = [
  { day: 'Monday', weekday: 'monday' },
  { day: 'Tuesday', weekday: 'tuesday' },
  { day: 'Wednesday', weekday: 'wednesday' },
  { day: 'Thursday', weekday: 'thursday' },
  { day: 'Friday', weekday: 'friday' },
  { day: 'Saturday', weekday: 'saturday' },
  { day: 'Sunday', weekday: 'sunday' },
];

interface BusinessHoursPermanentProps {
  isVisible: boolean;
  onClose: () => void;
  onSave?: (data: BusinessHoursDayData[]) => void;
  isSaveLoading?: boolean;
  permanentHours?: BusinessHourItem[];
}

export function BusinessHoursPermanent({
  isVisible,
  onClose,
  onSave,
  isSaveLoading,
  permanentHours = [],
}: BusinessHoursPermanentProps) {
  const [businessHours, setBusinessHours] = useState<Record<string, BusinessHoursDayData>>({});

  const handleDayChange = useCallback((data: BusinessHoursDayData) => {
    setBusinessHours((prev) => ({
      ...prev,
      [data.weekday]: data,
    }));
  }, []);

  const hasValidationErrors = useMemo(() => {
    return Object.values(businessHours).some((day) => day.hasError);
  }, [businessHours]);

  const handleSave = () => {
    if (hasValidationErrors) return;
    onSave?.(Object.values(businessHours));
  };

  return (
    <Drawer
      className={styles.businessHoursPermanent}
      open={isVisible}
      onClose={onClose}
      destroyOnHidden
      classNames={{
        wrapper: styles.drawerBody,
      }}
      footer={
        <Flex gap={12} justify="flex-end">
          <Button buttonVariant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={isSaveLoading}
            buttonVariant="primary"
            onClick={handleSave}
            disabled={hasValidationErrors}
          >
            Save
          </Button>
        </Flex>
      }
    >
      <p className={styles.permanentSectionTitle}>Regular Weekly Schedule</p>
      <Divider />

      <Flex vertical>
        {WEEKDAYS.map(({ day, weekday }) => {
          const dayDataArray = permanentHours.filter((h) => h.wday === weekday);
          const extractTime = (isoString: string) => {
            const match = isoString.match(/T(\d{2}:\d{2})/);
            return match ? match[1] : '';
          };

          const periods =
            dayDataArray.length > 0
              ? dayDataArray.map((dayData) => ({
                  start: dayData.start_time ? extractTime(dayData.start_time) : '08:00',
                  end: dayData.end_time ? extractTime(dayData.end_time) : '16:00',
                }))
              : undefined;

          const firstDayData = dayDataArray[0];

          return (
            <BusinessHoursDay
              key={weekday}
              day={day}
              weekday={weekday}
              initialIsClosed={
                firstDayData?.is_closed ?? (weekday === 'saturday' || weekday === 'sunday')
              }
              initialIsAllDay={firstDayData?.all_day ?? false}
              initialPeriods={periods}
              onChange={handleDayChange}
            />
          );
        })}
      </Flex>
    </Drawer>
  );
}
